import { useTrainerClientAchievements, useTrainerClientSummaries } from '../../api/queries';
import { AchievementIcon } from '../../components/AchievementIcon';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate, formatNumber } from '../../lib/format';
import { highlightMessageKey, isBlankWeek, weightDelta } from '../../lib/summary';
import type { WeeklySummaryRow } from '../../api/types';
import { Badge, Card, CardHeader, ProgressBar, SkeletonCard, SkeletonList, StatTile } from '../../ui';

/**
 * Podaci faze 3 na trenerovom ekranu klijenta.
 *
 * Klijent isti sažetak vidi na `/sazetak`, ali trener ga treba prije nego mu se
 * klijent javi — zato stoji na profilu klijenta, a ne kao zaseban ekran.
 * Računanje (prazan tjedan, razlika težine, rečenica za highlight) ide kroz
 * `lib/summary`, isto kao klijentov ekran; dvije verzije istog pravila bile bi
 * dvije različite istine.
 */
export function ClientWeeklySummaries({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const query = useTrainerClientSummaries(clientId);

  return (
    <section data-testid="client-weekly-summaries">
      <h2 className="eyebrow mb-1">{t('trainer.clientSummaries')}</h2>
      <p className="mb-3 text-[13px] text-ink-muted">{t('trainer.clientSummariesHelp')}</p>
      <QueryBoundary
        query={query}
        skeleton={<SkeletonCard />}
        isEmpty={(d) => d.items.filter((w) => !isBlankWeek(w)).length === 0}
        empty={{ message: t('trainer.clientSummariesEmpty') }}
      >
        {(data) => {
          const weeks = data.items.filter((w) => !isBlankWeek(w));
          const [latest, ...older] = weeks;
          if (!latest) return null;
          return (
            <div className="flex flex-col gap-3">
              <LatestWeek week={latest} />
              {older.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {older.map((week) => (
                    <li key={week.weekStart}>
                      <OlderWeek week={week} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        }}
      </QueryBoundary>
    </section>
  );
}

function LatestWeek({ week }: { week: WeeklySummaryRow }) {
  const { t, locale } = useTranslation();
  const delta = weightDelta(week);
  const highlights = week.highlights
    .map((key) => highlightMessageKey(key))
    .filter((key): key is string => key !== null);

  return (
    <Card raised data-testid="client-summary-latest">
      <CardHeader
        eyebrow={t('summary.lastWeek')}
        title={t('summary.weekOf', { date: formatDate(`${week.weekStart}T00:00:00.000Z`, locale) })}
        action={
          <Badge tone={week.consistencyPct >= 60 ? 'success' : 'warning'}>
            {t('summary.consistencyValue', { pct: week.consistencyPct })}
          </Badge>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={t('summary.workouts')}
          value={`${week.workoutsCompleted}/${week.workoutsPrescribed}`}
        />
        <StatTile
          label={t('summary.steps')}
          value={week.steps === null ? '—' : formatNumber(week.steps, locale)}
        />
        <StatTile
          label={t('summary.averageMinutes')}
          value={week.averageMinutes === null ? '—' : week.averageMinutes}
          {...(week.averageMinutes === null ? {} : { unit: 'min' })}
        />
        <StatTile
          label={t('dash.weight')}
          value={
            week.weightStart === null || week.weightEnd === null
              ? '—'
              : `${formatNumber(week.weightStart, locale, 1)} → ${formatNumber(week.weightEnd, locale, 1)}`
          }
          {...(week.weightStart === null || week.weightEnd === null ? {} : { unit: 'kg' })}
          {...(delta === null || delta === 0
            ? {}
            : {
                delta: {
                  value: `${delta > 0 ? '+' : ''}${formatNumber(delta, locale, 1)} kg`,
                  // Isti dogovor kao na klijentovom ekranu: manje kilograma je
                  // zeleno jer je to smjer koji je klijent tražio.
                  direction: delta < 0 ? ('down' as const) : ('up' as const),
                },
              })}
        />
      </div>

      <ProgressBar
        className="mt-5"
        value={week.consistencyPct}
        max={100}
        tone={week.consistencyPct >= 60 ? 'accent' : 'warning'}
        label={t('summary.consistency')}
        valueLabel={t('summary.consistencyValue', { pct: week.consistencyPct })}
      />

      {highlights.length > 0 && (
        <ul className="mt-5 flex flex-col gap-2" data-testid="client-summary-highlights">
          {highlights.map((key) => (
            <li
              key={key}
              className="rounded-md bg-bg-alt px-4 py-3 text-[14px] leading-relaxed text-ink"
            >
              {t(key)}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Stariji tjedni su redak, ne kartica — trener ih čita kao trend, ne pojedinačno. */
function OlderWeek({ week }: { week: WeeklySummaryRow }) {
  const { t, locale } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line-subtle bg-surface px-4 py-3 shadow-card">
      <span className="text-[14px] text-ink">
        {t('summary.weekOf', { date: formatDate(`${week.weekStart}T00:00:00.000Z`, locale) })}
      </span>
      <span className="flex items-center gap-3 text-[13px] text-ink-muted">
        <span>
          {t('summary.workouts')}: {week.workoutsCompleted}/{week.workoutsPrescribed}
        </span>
        <Badge tone={week.consistencyPct >= 60 ? 'success' : 'warning'}>
          {t('summary.consistencyValue', { pct: week.consistencyPct })}
        </Badge>
      </span>
    </div>
  );
}

/**
 * Postignuća klijenta. Server šalje samo otključana i sam ih prevodi — ovdje se
 * ne izmišlja ni natpis ni ikona, kao ni na klijentovom ekranu.
 */
export function ClientAchievements({ clientId }: { clientId: string }) {
  const { t, locale } = useTranslation();
  const query = useTrainerClientAchievements(clientId);

  return (
    <section data-testid="client-achievements">
      <h2 className="eyebrow mb-3">{t('trainer.clientAchievements')}</h2>
      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={2} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('trainer.clientAchievementsEmpty') }}
      >
        {(data) => (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {data.items.map((a) => (
              <li
                key={a.key}
                data-testid="client-achievement"
                className="flex flex-col items-center gap-2 rounded-xl border border-line-subtle bg-surface px-4 py-5 text-center shadow-card"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-pill bg-accent-light text-accent-deep">
                  <AchievementIcon name={a.icon} size={22} />
                </span>
                <span className="text-[14px] leading-snug text-ink">{a.label}</span>
                <span className="text-[12px] text-ink-muted">
                  {formatDate(a.unlockedAt, locale)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </QueryBoundary>
    </section>
  );
}
